import React from 'react';

import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/macro';
import { v4 as uuidv4 } from 'uuid';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import RestoreIcon from '@mui/icons-material/RestoreOutlined';
import Tooltip from '@mui/material/Tooltip';

import Checkbox from '../../../misc/Checkbox';
import FormInlineButton from '../../../misc/FormInlineButton';
import Select from '../../../misc/Select';
import ytOAuth from '../../../utils/ytOAuth';
import { fetchYtTitles } from '../../../utils/ytdlp';
import { fetchYoutubeSnippet } from '../../../utils/youtube';

const id = 'youtube';
const name = 'YouTube Live';
const version = '1.4';
const stream_key_link = 'https://www.youtube.com/live_dashboard';
const description = (
	<Trans>
		Transmits your video as RTMP. Connect a YouTube channel account using the global OAuth2 credentials from Settings → Integrations,
		then create a live broadcast to get the stream key automatically.{' '}
		<Link color="secondary" target="_blank" href="https://creatoracademy.youtube.com/">
			Creator Academy
		</Link>
	</Trans>
);
const image_copyright = (
	<Trans>
		More about YouTube's copyright{' '}
		<Link color="secondary" target="_blank" href="https://www.youtube.com/howyoutubeworks/policies/copyright/#support-and-troubleshooting">
			here
		</Link>
		.
	</Trans>
);

const author = {
	creator: { name: 'datarhei', link: 'https://github.com/datarhei' },
	maintainer: { name: 'datarhei', link: 'https://github.com/datarhei' },
};

const category = 'platform';
const requires = {
	protocols: ['rtmps', 'https'],
	formats: ['flv', 'hls'],
	codecs: {
		audio: ['aac', 'mp3'],
		video: ['h264', 'hevc', 'av1'],
	},
};

const YT_API = 'https://www.googleapis.com/youtube/v3';

function ServiceIcon(props) {
	return <FontAwesomeIcon icon={faYoutube} style={{ color: '#FF0000' }} {...props} />;
}

function init(settings) {
	return {
		mode: 'rtmps',
		stream_key: '',
		primary: true,
		backup: false,
		title: '',
		description: '',
		// Datos de la cuenta conectada a ESTA publication
		account_key: '',           // clave en ytOAuth store (channel_id)
		account_label: '',         // nombre amigable del canal
		oauth_broadcast_id: '',
		oauth_stream_id: '',
		privacy_status: 'public',
		scheduled_start: '',
		...settings,
	};
}

function Service(props) {
	const settings = init(props.settings);

	const [$loading, setLoading] = React.useState(false);
	const [$apiError, setApiError] = React.useState('');
	const [$apiSuccess, setApiSuccess] = React.useState('');
	const [$connecting, setConnecting] = React.useState(false);
	const [$globalCreds, setGlobalCreds] = React.useState(() => ytOAuth.getCredentials());


	// Sincronizar credenciales y cuentas desde el servidor al montar
	React.useEffect(() => {
		(async () => {
			try {
				const creds = await ytOAuth.loadCredentialsFromServer();
				setGlobalCreds(creds);
				await ytOAuth.loadAccountsFromServer();
			} catch (_) {}
		})();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Credenciales globales guardadas en Settings → Integrations
	const globalCreds = $globalCreds;
	const hasGlobalCreds = !!(globalCreds.client_id && globalCreds.client_secret);

	// Pre-fill title/description desde metadata
	if (!settings.title && props.metadata && props.metadata.name) {
		settings.title = props.metadata.name;
	}
	if (!settings.description && props.metadata && props.metadata.description) {
		settings.description = props.metadata.description;
	}
	if (!settings.scheduled_start) {
		const d = new Date(Date.now() + 5 * 60 * 1000);
		settings.scheduled_start = d.toISOString().slice(0, 16);
	}

	// Cuenta conectada a ESTA publication (guardada en ytOAuth store)
	const connectedAccount = settings.account_key ? ytOAuth.getAccount(settings.account_key) : null;
	// isConnected: tiene token local O tiene token en servidor (has_access_token)
	const isConnected = !!(connectedAccount && (connectedAccount.access_token || connectedAccount.has_access_token));
	const isTokenExpired = isConnected && connectedAccount && connectedAccount.token_expiry
		? Date.now() > connectedAccount.token_expiry - 60000
		: false;

	const pushSettings = (s) => props.onChange(createOutput(s), s);

	const handleChange = (what) => (event) => {
		const value = event && event.target ? event.target.value : event;
		setApiError('');
		if (['primary', 'backup'].includes(what)) {
			settings[what] = !settings[what];
		} else {
			settings[what] = value;
		}
		pushSettings(settings);
	};

	const createOutput = (s) => {
		const outputs = [];
		if (!s.stream_key) return outputs;
		if (s.mode === 'rtmps') {
			let options = ['-f', 'flv'];
			if (props.skills && props.skills.ffmpeg && props.skills.ffmpeg.version_major >= 6) {
				const codecs = [];
				if (props.skills.codecs && props.skills.codecs.video && props.skills.codecs.video.includes('hevc')) codecs.push('hvc1');
				if (props.skills.codecs && props.skills.codecs.video && props.skills.codecs.video.includes('av1')) codecs.push('av01');
				if (codecs.length) options.push('-rtmp_enhanced_codecs', codecs.join(','));
			}
			if (s.primary) outputs.push({ address: 'rtmps://a.rtmp.youtube.com/live2/' + s.stream_key, options: options.slice() });
			if (s.backup) outputs.push({ address: 'rtmps://b.rtmp.youtube.com/live2?backup=1/' + s.stream_key, options: options.slice() });
		} else if (s.mode === 'hls') {
			const hlsName = uuidv4();
			const options = ['-f','hls','-start_number','0','-hls_time','2','-hls_delete_threshold','3',
				'-hls_list_size','5','-hls_flags','append_list','-hls_segment_type','mpegts',
				'-http_persistent','1','-y','-method','PUT'];
			if (s.primary) {
				const base = `https://a.upload.youtube.com/http_upload_hls?cid=${s.stream_key}&copy=0&file=`;
				outputs.push({ address: base + hlsName + '.m3u8', options: [...options, '-hls_segment_filename', base + hlsName + '_%d.ts'] });
			}
			if (s.backup) {
				const base = `https://b.upload.youtube.com/http_upload_hls?cid=${s.stream_key}&copy=1&file=`;
				outputs.push({ address: base + hlsName + '.m3u8', options: [...options, '-hls_segment_filename', base + hlsName + '_%d.ts'] });
			}
		}
		return outputs;
	};

	// ─── Conectar cuenta de YouTube para ESTA publication ─────────────────
	const handleConnectAccount = () => {
		setApiError('');
		setApiSuccess('');

		if (!hasGlobalCreds) {
			setApiError('No global OAuth2 credentials found. Go to Settings → Integrations to enter your Client ID and Client Secret first.');
			return;
		}

		// Usar YT_OAUTH_CALLBACK_URL si está definida (producción con dominio real)
		// De lo contrario construir desde window.location.origin (desarrollo)
		const _rtCfg = (typeof window !== 'undefined' && window.__RESTREAMER_CONFIG__) || {};
		const redirectUri = _rtCfg.YT_OAUTH_CALLBACK_URL
			? _rtCfg.YT_OAUTH_CALLBACK_URL
			: window.location.origin + '/oauth2callback';
		const oauthState = btoa(JSON.stringify({ service: 'youtube', ts: Date.now() }));
		const authUrl = 'https://accounts.google.com/o/oauth2/v2/auth?' + new URLSearchParams({
			client_id:     globalCreds.client_id,
			redirect_uri:  redirectUri,
			response_type: 'code',
			scope:         'https://www.googleapis.com/auth/youtube.force-ssl',
			access_type:   'offline',
			prompt:        'consent',
			state:         oauthState,
		}).toString();

		// Abrir popup centrado
		const w = 600, h = 700;
		const left = Math.round(window.screenX + (window.outerWidth  - w) / 2);
		const top  = Math.round(window.screenY + (window.outerHeight - h) / 2);
		const authWindow = window.open(
			authUrl,
			'youtube_oauth_pub',
			`width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`
		);

		if (!authWindow) {
			setApiError('Popup was blocked. Please allow popups for this site and try again.');
			return;
		}

		setConnecting(true);

		// Timeout de seguridad — 10 minutos
		const timeoutId = setTimeout(() => {
			window.removeEventListener('message', listener);
			setConnecting(false);
			setApiError('Authorization timed out. Please try again.');
		}, 10 * 60 * 1000);

		const listener = async (event) => {
			// Aceptar mensajes del mismo origen o del popup
			if (!event.data || event.data.service !== 'youtube') return;
			// Ignorar el ACK que nosotros enviamos al popup
			if (event.data.ack === true) return;

			// Quitar el listener y timeout
			window.removeEventListener('message', listener);
			clearTimeout(timeoutId);

			// Enviar ACK al popup para que se cierre
			// Enviar ACK al popup para que se cierre (acceder a .closed dentro de try/catch
			// para evitar warnings cuando COOP/COEP bloquea el acceso a propiedades cross-origin)
			try {
				let isClosed = false;
				try { isClosed = !!authWindow.closed; } catch (e) { isClosed = false; }
				if (authWindow && !isClosed) {
					try { authWindow.postMessage({ service: 'youtube', ack: true }, '*'); } catch (e) { /* ignore */ }
				}
				// Dar 1 s al popup para procesar el ACK y cerrarse solo. Intento de cierre protegido.
				setTimeout(() => { try { if (!authWindow.closed) authWindow.close(); } catch (e) { /* ignore */ } }, 1200);
			} catch (e) { /* ignore */ }

			if (event.data.error) {
				setApiError('Authorization denied: ' + event.data.error);
				setConnecting(false);
				return;
			}

			if (event.data.code) {
				try {
					// Usar el redirect_uri que envió el callback (URL exacta de la página de callback)
					// Esto garantiza que coincida exactamente con el registrado en Google Console
					const callbackRedirectUri = event.data.redirect_uri || redirectUri;

					// Intercambiar code por tokens vía servidor (incluye IDs de Restreamer)
					const exchangeResp = await fetch('/fb-server/yt/exchange', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							code:                      event.data.code,
							redirect_uri:              callbackRedirectUri,
							restreamer_channel_id:     props.channelId     || '',
							restreamer_publication_id: props.publicationId || '',
						}),
					});
					const exchangeData = await exchangeResp.json();

					if (exchangeData.error) {
						// Fallback client-side si el servidor no tiene credentials
						const localCreds = globalCreds;
						if (!localCreds.client_id || !localCreds.client_secret) {
							throw new Error(exchangeData.error);
						}
						const resp = await fetch('https://oauth2.googleapis.com/token', {
							method: 'POST',
							headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
							body: new URLSearchParams({
								code:          event.data.code,
								client_id:     localCreds.client_id,
								client_secret: localCreds.client_secret,
								redirect_uri:  callbackRedirectUri,
								grant_type:    'authorization_code',
							}).toString(),
						});
						const tokenData = await resp.json();
						if (tokenData.error) throw new Error(tokenData.error_description || tokenData.error);

						const channelInfo = await ytOAuth.fetchChannelInfo(tokenData.access_token);
						const accountKey  = channelInfo.channel_id || ('yt_' + Date.now());
						const channelName = channelInfo.channel_title || accountKey;

						await ytOAuth.saveAccount(accountKey, {
							label:                     channelName,
							access_token:              tokenData.access_token,
							refresh_token:             tokenData.refresh_token || '',
							token_expiry:              Date.now() + (tokenData.expires_in || 3600) * 1000,
							channel_title:             channelInfo.channel_title || '',
							channel_id:                channelInfo.channel_id    || accountKey,
							restreamer_channel_id:     props.channelId           || '',
							restreamer_publication_id: props.publicationId       || '',
						});

						settings.account_key   = accountKey;
						settings.account_label = channelName;
						pushSettings(settings);
						setApiSuccess('✅ Channel "' + channelName + '" connected successfully!');
					} else {
						// Éxito por servidor — cuenta ya guardada en config.json con IDs de Restreamer
						const acc = exchangeData.account;
						const accountKey  = acc.account_key || acc.channel_id;
						const channelName = acc.label || acc.channel_title || accountKey;

						ytOAuth.saveAccountSync(accountKey, {
							...acc,
							restreamer_channel_id:     props.channelId     || '',
							restreamer_publication_id: props.publicationId || '',
						});

						settings.account_key   = accountKey;
						settings.account_label = channelName;
						pushSettings(settings);
						setApiSuccess('✅ Channel "' + channelName + '" connected successfully!');
					}
				} catch (err) {
					setApiError('❌ ' + err.message);
				} finally {
					setConnecting(false);
				}
			}
		};

		window.addEventListener('message', listener);

			// Detectar si el usuario cierra el popup manualmente. Acceder a authWindow.closed
			// dentro de try/catch para evitar COOP/COEP warnings; si el acceso está bloqueado,
			// confiar en el listener/postMessage para resolver el flujo.
			const pollClosed = setInterval(() => {
				try {
					if (!authWindow || authWindow.closed) {
						clearInterval(pollClosed);
						// Si todavía estamos esperando (el listener no disparó), limpiar
						window.removeEventListener('message', listener);
						clearTimeout(timeoutId);
						setConnecting(false);
					}
				} catch (e) {
					// Acceso bloqueado por políticas COOP/COEP — ignorar y dejar que el listener maneje el cierre
				}
			}, 500);
	};

	// ─── Pegar token manualmente ──────────────────────────────────────────
	const handleManualToken = async () => {
		setApiError('');
		setApiSuccess('');
		const token = window.prompt(
			'Paste your YouTube OAuth2 Access Token.\n\nGet it from:\nhttps://developers.google.com/oauthplayground\n\nScope: https://www.googleapis.com/auth/youtube.force-ssl'
		);
		if (!token || !token.trim()) return;

		setConnecting(true);
		try {
			const channelInfo = await ytOAuth.fetchChannelInfo(token.trim());
			const accountKey = channelInfo.channel_id || ('yt_manual_' + Date.now());

			await ytOAuth.saveAccount(accountKey, {
				label:                     channelInfo.channel_title || 'Manual token',
				access_token:              token.trim(),
				refresh_token:             '',
				token_expiry:              Date.now() + 3600 * 1000,
				channel_title:             channelInfo.channel_title || '',
				channel_id:                channelInfo.channel_id    || accountKey,
				restreamer_channel_id:     props.channelId           || '',
				restreamer_publication_id: props.publicationId       || '',
			});

			settings.account_key = accountKey;
			settings.account_label = channelInfo.channel_title || accountKey;
			pushSettings(settings);
			setApiSuccess('✅ Channel "' + (channelInfo.channel_title || accountKey) + '" connected (manual token — expires in 1h).');
		} catch (err) {
			setApiError('Error: ' + err.message);
		} finally {
			setConnecting(false);
		}
	};

	// ─── Desconectar cuenta ───────────────────────────────────────────────
	const handleDisconnect = () => {
		settings.account_key = '';
		settings.account_label = '';
		settings.stream_key = '';
		settings.oauth_broadcast_id = '';
		settings.oauth_stream_id = '';
		pushSettings(settings);
		setApiError('');
		setApiSuccess('');
	};

	// ─── Crear LiveBroadcast + LiveStream + Bind ───────────────────────────
	const handleCreateLive = async () => {
		setApiError('');
		setApiSuccess('');
		if (!isConnected) {
			setApiError('Connect a YouTube channel account first.');
			return;
		}
		if (!settings.title) {
			setApiError('Enter a stream title first.');
			return;
		}

		setLoading(true);
		try {
			const token = await ytOAuth.getValidToken(settings.account_key);
			const headers = { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' };

			const startTime = settings.scheduled_start
				? new Date(settings.scheduled_start).toISOString()
				: new Date(Date.now() + 5 * 60 * 1000).toISOString();

			// 1) LiveBroadcast
			const bResp = await fetch(`${YT_API}/liveBroadcasts?part=snippet,status,contentDetails`, {
				method: 'POST', headers,
				body: JSON.stringify({
					snippet: { title: settings.title, description: settings.description || '', scheduledStartTime: startTime },
					status: { privacyStatus: settings.privacy_status || 'public', selfDeclaredMadeForKids: false },
					contentDetails: { enableAutoStart: true, enableAutoStop: true, enableDvr: true, recordFromStart: true },
				}),
			});
			const bData = await bResp.json();
			if (!bResp.ok) throw new Error('Broadcast: ' + (bData.error ? bData.error.message : `HTTP ${bResp.status}`));
			const broadcastId = bData.id;

			// 2) LiveStream — contentDetails debe estar en ?part= para poder enviarlo en el body
			const sResp = await fetch(`${YT_API}/liveStreams?part=snippet,cdn,status,contentDetails`, {
				method: 'POST', headers,
				body: JSON.stringify({
					snippet: { title: settings.title },
					cdn: { frameRate: 'variable', ingestionType: 'rtmp', resolution: 'variable' },
					contentDetails: { isReusable: false },
				}),
			});
			const sData = await sResp.json();
			if (!sResp.ok) throw new Error('Stream: ' + (sData.error ? sData.error.message : `HTTP ${sResp.status}`));
			const streamId = sData.id;
			const streamKey = sData.cdn?.ingestionInfo?.streamName || '';

			// 3) Bind
			const bindResp = await fetch(
				`${YT_API}/liveBroadcasts/bind?id=${broadcastId}&part=id,contentDetails&streamId=${streamId}`,
				{ method: 'POST', headers }
			);
			if (!bindResp.ok) {
				const bd = await bindResp.json();
				throw new Error('Bind: ' + (bd.error ? bd.error.message : `HTTP ${bindResp.status}`));
			}

			settings.oauth_broadcast_id = broadcastId;
			settings.oauth_stream_id = streamId;
			settings.stream_key = streamKey;
			pushSettings(settings);

			setApiSuccess(
				`✅ Broadcast created on "${connectedAccount.channel_title || connectedAccount.label}"!\n` +
				`• Stream key filled automatically\n• Privacy: ${settings.privacy_status}\n• Starts: ${startTime}`
			);
		} catch (err) {
			setApiError('❌ ' + err.message);
		} finally {
			setLoading(false);
		}
	};

	const allowRTMPS = props.skills && props.skills.protocols && props.skills.protocols.includes('rtmps') && props.skills.formats && props.skills.formats.includes('flv');
	const allowHLS = props.skills && props.skills.protocols && props.skills.protocols.includes('https') && props.skills.formats && props.skills.formats.includes('hls');

	return (
		<Grid container spacing={2}>
			{/* ── Banner de cuenta conectada ──────────────────────────── */}
			{isConnected ? (
				<Grid item xs={12}>
					<div style={{
						display: 'flex',
						alignItems: 'center',
						gap: 10,
						background: 'rgba(66,183,42,0.08)',
						border: '1px solid rgba(66,183,42,0.35)',
						borderRadius: 6,
						padding: '8px 14px',
					}}>
						<span style={{ fontSize: '1.2rem' }}>🟢</span>
						<div style={{ lineHeight: 1.4 }}>
							<Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.88rem' }}>
								YouTube channel connected:{' '}
								<span style={{ color: '#4caf50' }}>
									{connectedAccount.channel_title || connectedAccount.label || connectedAccount.channel_id}
								</span>
							</Typography>
							{connectedAccount.channel_id && (
								<Typography variant="caption" style={{ color: '#888', display: 'block' }}>
									Channel ID: {connectedAccount.channel_id}
									{isTokenExpired && (
										<span style={{ color: '#ff9800', marginLeft: 8 }}>⚠️ Token expired — reconnect</span>
									)}
								</Typography>
							)}
						</div>
					</div>
				</Grid>
			) : (
				<Grid item xs={12}>
					<div style={{
						display: 'flex',
						alignItems: 'center',
						gap: 10,
						background: 'rgba(255,152,0,0.08)',
						border: '1px solid rgba(255,152,0,0.35)',
						borderRadius: 6,
						padding: '8px 14px',
					}}>
						<span style={{ fontSize: '1.2rem' }}>⚠️</span>
						<Typography variant="body2" style={{ fontSize: '0.85rem', color: '#ff9800' }}>
							{hasGlobalCreds
								? 'No YouTube channel connected. Use the section below to authorize with Google.'
								: <><span>No OAuth2 credentials found. </span><Link color="secondary" href="/#/settings/integrations" target="_blank">Go to Settings → Integrations</Link><span> to add your Client ID and Client Secret.</span></>
							}
						</Typography>
					</div>
				</Grid>
			)}

			{/* ── Modo y stream key ──────────────────────────────────── */}
			<Grid item xs={12}>
				<Select label="Delivering mode" value={settings.mode} onChange={handleChange('mode')}>
					{allowRTMPS && <MenuItem value="rtmps">RTMP</MenuItem>}
					{allowHLS && <MenuItem value="hls">HLS</MenuItem>}
				</Select>
			</Grid>
			<Grid item xs={12} md={9}>
				<MuiTextField
					variant="outlined"
					fullWidth
					label="Stream key"
					helperText="Filled automatically after creating a live broadcast, or enter manually"
					value={settings.stream_key}
					onChange={handleChange('stream_key')}
				/>
			</Grid>
			<Grid item xs={12} md={3}>
				<FormInlineButton target="blank" href={stream_key_link} component="a">
					<Trans>GET</Trans>
				</FormInlineButton>
			</Grid>
			<Grid item xs={12}>
				<Checkbox label={<Trans>Primary stream</Trans>} checked={settings.primary} onChange={handleChange('primary')} />
				<Checkbox label={<Trans>Backup stream</Trans>} checked={settings.backup} onChange={handleChange('backup')} />
			</Grid>

			{/* ── Importar título/descripción desde URL de YouTube ──────── */}
			<Grid item xs={12}>
				<Typography variant="h4" style={{ marginBottom: 6 }}>
					<Trans>Stream Settings</Trans>
				</Typography>
				<Typography variant="caption" style={{ color: '#aaa', display: 'block', marginBottom: 0 }}>
					<Trans>Optionally paste a YouTube URL or Video ID to auto-fill the title and description below.</Trans>
				</Typography>
			</Grid>
			{/* YouTube URL input removed — Reset will restore from stored metadata */}

			{/* ── Título y descripción ────────────────────────────────── */}
			<Grid item xs={12}>
				<MuiTextField
					variant="outlined"
					fullWidth
					label={<Trans>Stream title</Trans>}
					placeholder={props.metadata && props.metadata.name ? props.metadata.name : ''}
					value={settings.title}
					onChange={handleChange('title')}
					InputProps={props.metadata && props.metadata.name ? {
						endAdornment: (
							<InputAdornment position="end">
								<Tooltip title={<Trans>Reset to stream title</Trans>}>
									<IconButton size="small" onClick={async () => {
											const meta = props.metadata || {};
											const t = meta.name || meta.title || '';
											const d = meta.description || '';
											if (!t && !d) return;
											if (t) settings.title = t;
											if (d) settings.description = d;
											pushSettings(settings);
											try {
												if (props.restreamer && typeof props.restreamer.SetEgressMetadata === 'function') {
													await props.restreamer.SetEgressMetadata(props.channelId, props.publicationId, { name: settings.title || '', description: settings.description || '' });
												}
											} catch (err) {
												console.warn('[reset] failed to save metadata', err);
											}
										}}>
										<RestoreIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</InputAdornment>
						),
					} : undefined}
				/>
			</Grid>
			<Grid item xs={12}>
				<MuiTextField
					variant="outlined"
					fullWidth
					multiline
					rows={3}
					label={<Trans>Stream description</Trans>}
					placeholder={props.metadata && props.metadata.description ? props.metadata.description : ''}
					value={settings.description}
					onChange={handleChange('description')}
					InputProps={props.metadata && props.metadata.description ? {
						endAdornment: (
							<InputAdornment position="end" style={{ alignSelf: 'flex-start', marginTop: 10 }}>
								<Tooltip title={<Trans>Reset to stream description</Trans>}>
									<IconButton size="small" onClick={async () => {
											const meta = props.metadata || {};
											const t = meta.name || meta.title || '';
											const d = meta.description || '';
											if (!t && !d) return;
											if (t) settings.title = t;
											if (d) settings.description = d;
											pushSettings(settings);
											try {
												if (props.restreamer && typeof props.restreamer.SetEgressMetadata === 'function') {
													await props.restreamer.SetEgressMetadata(props.channelId, props.publicationId, { name: settings.title || '', description: settings.description || '' });
												}
											} catch (err) {
												console.warn('[reset] failed to save metadata', err);
											}
										}}>
										<RestoreIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</InputAdornment>
						),
					} : undefined}
				/>
			</Grid>

			{/* ── Cuenta de canal YouTube ─────────────────────────────── */}
			<Grid item xs={12}>
				<Typography variant="h4" style={{ marginTop: 4 }}>
					YouTube channel account
				</Typography>
				{!hasGlobalCreds && (
					<Typography variant="caption" style={{ color: '#ff9800', display: 'block', marginTop: 4 }}>
						⚠️ No OAuth2 credentials found. Go to{' '}
						<Link color="secondary" href="/#/settings/integrations" target="_blank">
							Settings → Integrations
						</Link>{' '}
						to enter your Client ID and Client Secret first.
					</Typography>
				)}
			</Grid>

			{/* Estado de la cuenta conectada */}
			{isConnected ? (
				<React.Fragment>
					<Grid item xs={12}>
						{isTokenExpired ? (
							<Typography variant="body2" style={{ color: '#ff9800', fontSize: '0.82rem' }}>
								⚠️ Token expired for <strong>{connectedAccount.channel_title || connectedAccount.label}</strong>. Reconnect the account.
							</Typography>
						) : (
							<Typography variant="body2" style={{ color: '#4caf50', fontSize: '0.82rem' }}>
								🟢 Connected: <strong>{connectedAccount.channel_title || connectedAccount.label}</strong>
								{connectedAccount.channel_id && (
									<span style={{ marginLeft: 8, color: '#888', fontSize: '0.75rem' }}>{connectedAccount.channel_id}</span>
								)}
							</Typography>
						)}
					</Grid>
					<Grid item xs={12}>
						<FormInlineButton onClick={handleDisconnect}>
							Disconnect channel
						</FormInlineButton>
					</Grid>
				</React.Fragment>
			) : (
				<React.Fragment>
					<Grid item xs={12} md={6}>
						<FormInlineButton
							fullWidth
							onClick={handleConnectAccount}
							disabled={$connecting || !hasGlobalCreds}
						>
							{$connecting ? '⏳' : '🔑'} Authorize with Google
						</FormInlineButton>
						<Typography variant="caption" style={{ display: 'block', marginTop: 4, color: '#888', textAlign: 'center' }}>
							Uses credentials from Settings → Integrations
						</Typography>
					</Grid>
					<Grid item xs={12} md={6}>
						<FormInlineButton
							fullWidth
							onClick={handleManualToken}
							disabled={$connecting}
						>
							📋 Paste token manually
						</FormInlineButton>
						<Typography variant="caption" style={{ display: 'block', marginTop: 4, color: '#888', textAlign: 'center' }}>
							OAuth Playground token — expires in 1h
						</Typography>
					</Grid>
				</React.Fragment>
			)}

			{/* ── Opciones del broadcast (solo si está conectado) ─────── */}
			{isConnected && (
				<React.Fragment>
					<Grid item xs={12} md={6}>
						<Select
							label="Privacy status"
							value={settings.privacy_status}
							onChange={handleChange('privacy_status')}
						>
							<MenuItem value="public">Public</MenuItem>
							<MenuItem value="unlisted">Unlisted</MenuItem>
							<MenuItem value="private">Private</MenuItem>
						</Select>
					</Grid>
					<Grid item xs={12} md={6}>
						<MuiTextField
							variant="outlined"
							fullWidth
							label="Scheduled start (local time)"
							type="datetime-local"
							value={settings.scheduled_start}
							onChange={handleChange('scheduled_start')}
							InputLabelProps={{ shrink: true }}
							helperText="Leave default to start ~5 min from now"
						/>
					</Grid>
					<Grid item xs={12}>
						<FormInlineButton
							fullWidth
							onClick={handleCreateLive}
							disabled={$loading || !settings.title}
						>
							{$loading ? 'Creating broadcast...' : '🎬 Create Live Broadcast & get stream key'}
						</FormInlineButton>
						{settings.oauth_broadcast_id && (
							<Typography variant="caption" style={{ color: '#888', display: 'block', marginTop: 4 }}>
								Broadcast ID: {settings.oauth_broadcast_id}
							</Typography>
						)}
					</Grid>
				</React.Fragment>
			)}

			{/* Error / Success */}
			{$apiError && (
				<Grid item xs={12}>
					<Typography variant="body2" style={{ color: '#f44336', whiteSpace: 'pre-wrap', fontSize: '0.82rem' }}>
						{$apiError}
					</Typography>
				</Grid>
			)}
			{$apiSuccess && (
				<Grid item xs={12}>
					<Typography variant="body2" style={{ color: '#4caf50', whiteSpace: 'pre-wrap', fontSize: '0.82rem' }}>
						{$apiSuccess}
					</Typography>
				</Grid>
			)}
		</Grid>
	);
}

Service.defaultProps = {
	settings: {},
	skills: {},
	metadata: {},
	streams: [],
	onChange: function (output, settings) {},
};

export { id, name, version, stream_key_link, description, image_copyright, author, category, requires, ServiceIcon as icon, Service as component };
